import React, { useState } from 'react';
import {
  Box,
  Text,
  TextInput,
  ActionIcon,
  Stack,
  Table,
  Group,
  Card,
  Alert,
} from '@mantine/core';
import { IconPlus, IconMinus, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ITable } from '@/types/Table';
import { useMediaQuery } from '@mantine/hooks';
import { validateAndCorrectMetadataKey } from '@/services/Validators';

interface MetadataTableEditorProps {
  value?: ITable;
  onChange: (metadata: ITable) => void;
  productId?: string; // Optional - for editing existing products
}

/** Price field keys that may show the €299 example placeholder. */
function isPriceFieldKey(key: string): boolean {
  const normalized = key.trim().toLowerCase();
  return normalized === 'price' || normalized === 'preis';
}

/**
 * Immutable update of one metadata row's data map.
 * Avoids mutating nested objects shared with the edit baseline (dirty detection).
 */
function withUpdatedRowData(
  metadata: ITable,
  actualRowKey: string,
  updater: (data: Record<string, string>) => Record<string, string>
): ITable {
  const prevRow = metadata.rows?.[actualRowKey] ?? {
    pNetId: actualRowKey,
    data: {},
  };
  return {
    ...metadata,
    rows: {
      ...metadata.rows,
      [actualRowKey]: {
        ...prevRow,
        pNetId: prevRow.pNetId || actualRowKey,
        data: updater({ ...(prevRow.data || {}) }),
      },
    },
  };
}

export function MetadataTableEditor({ value, onChange, productId }: MetadataTableEditorProps) {
  const { t } = useTranslation();
  const [newKey, setNewKey] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [keyValidationError, setKeyValidationError] = useState<string | null>(null);

  // Responsive breakpoint for mobile layout
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Use "default" for new products, actual productId for existing ones
  const rowKey = productId || 'default';

  // Initialize metadata if not provided
  const metadata = value || {
    _id: '',
    name: '',
    columns: {},
    rows: {
      [rowKey]: {
        pNetId: rowKey,
        data: {},
      },
    },
  };

  // Convert data object to array for easier editing
  // Handle both cases: existing products with actual ID, and new products with "default"
  const existingRows = metadata.rows || {};
  const actualRowKey =
    Object.keys(existingRows).find(
      (key) =>
        existingRows[key] &&
        existingRows[key].data &&
        Object.keys(existingRows[key].data).length > 0
    ) || rowKey;

  const dataEntries = Object.entries(existingRows[actualRowKey]?.data || {}).map(
    ([key, value]) => ({
      key,
      value: String(value),
    })
  );

  const valuePlaceholderForKey = (key: string) =>
    isPriceFieldKey(key)
      ? t('editProduct.specificationPricePlaceholder')
      : t('editProduct.specificationValuePlaceholder');

  const handleAddKey = () => {
    if (!newKey.trim()) return;

    // Validate and correct the new key
    const validation = validateAndCorrectMetadataKey(newKey);

    if (validation.error) {
      setKeyValidationError(validation.error);
      return;
    }

    onChange(
      withUpdatedRowData(metadata, actualRowKey, (data) => ({
        ...data,
        [validation.correctedKey]: '',
      }))
    );

    // Start editing the new key
    setEditingKey(validation.correctedKey);
    setEditingValue('');
    setNewKey('');
    setKeyValidationError(null);
  };

  const handleDeleteKeyValue = (keyToDelete: string) => {
    onChange(
      withUpdatedRowData(metadata, actualRowKey, (data) => {
        const next = { ...data };
        delete next[keyToDelete];
        return next;
      })
    );
  };

  const handleStartEdit = (key: string, value: string) => {
    setEditingKey(key);
    setEditingValue(value);
    setKeyValidationError(null);
  };

  const handleSaveEdit = () => {
    if (editingKey && editingValue.trim() !== metadata.rows[actualRowKey]?.data[editingKey]) {
      onChange(
        withUpdatedRowData(metadata, actualRowKey, (data) => ({
          ...data,
          [editingKey]: editingValue.trim(),
        }))
      );
    }
    setEditingKey(null);
    setEditingValue('');
    setKeyValidationError(null);
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditingValue('');
  };

  return (
    <Box>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('editProduct.addCustomSpecifications')}
        </Text>

        {/* Validation error display */}
        {keyValidationError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title={t('editProduct.validationError')}
            color="red"
            variant="light"
          >
            {t(keyValidationError)}
          </Alert>
        )}

        {/* Specifications table */}
        {dataEntries.length > 0 && (
          <>
            {/* Desktop: Table Layout */}
            {!isMobile && (
              <Table>
                <Table.Tbody>
                  {dataEntries.map(({ key, value }) => (
                    <Table.Tr key={key} style={{ minHeight: '60px' }}>
                      <Table.Td
                        style={{
                          fontWeight: 500,
                          minWidth: '140px',
                          paddingRight: '20px',
                          verticalAlign: 'top',
                          fontSize: '13px',
                          paddingTop: '12px',
                          paddingBottom: '12px',
                        }}
                      >
                        {key}
                      </Table.Td>
                      <Table.Td
                        style={{
                          width: '100%',
                          position: 'relative',
                          verticalAlign: 'top',
                          paddingTop: '12px',
                          paddingBottom: '12px',
                        }}
                      >
                        {editingKey === key ? (
                          <TextInput
                            value={editingValue}
                            onChange={(event) => setEditingValue(event.currentTarget.value)}
                            size="sm"
                            style={{ width: 'calc(100% - 40px)' }}
                            autoFocus
                            placeholder={valuePlaceholderForKey(key)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSaveEdit();
                              } else if (event.key === 'Escape') {
                                event.preventDefault();
                                handleCancelEdit();
                              }
                            }}
                            onBlur={handleSaveEdit}
                          />
                        ) : (
                          <Group
                            justify="space-between"
                            align="flex-start"
                            style={{ width: '100%', minHeight: '32px' }}
                          >
                            <Text
                              size="sm"
                              style={{
                                flex: 1,
                                cursor: 'pointer',
                                maxWidth: 'calc(100% - 40px)',
                                lineHeight: '1.4',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                              }}
                              onClick={() => handleStartEdit(key, value || '')}
                            >
                              {value || t('editProduct.clickToAddValue')}
                            </Text>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="red"
                              onClick={() => handleDeleteKeyValue(key)}
                              style={{
                                position: 'absolute',
                                right: '0',
                                top: '12px',
                                flexShrink: 0,
                              }}
                            >
                              <IconMinus size={14} />
                            </ActionIcon>
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}

            {/* Mobile: Card Layout */}
            {isMobile && (
              <Stack gap="xs">
                {dataEntries.map(({ key, value }) => (
                  <Card key={key} withBorder padding="xs" radius="sm">
                    <Group justify="space-between" align="center">
                      <Box style={{ flex: 1 }}>
                        <Text size="xs" fw={600} c="dimmed" mb={4} style={{ fontSize: '11px' }}>
                          {key}
                        </Text>
                        {editingKey === key ? (
                          <TextInput
                            value={editingValue}
                            onChange={(event) => setEditingValue(event.currentTarget.value)}
                            size="xs"
                            autoFocus
                            placeholder={valuePlaceholderForKey(key)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSaveEdit();
                              } else if (event.key === 'Escape') {
                                event.preventDefault();
                                handleCancelEdit();
                              }
                            }}
                            onBlur={handleSaveEdit}
                          />
                        ) : (
                          <Text
                            size="sm"
                            style={{ cursor: 'pointer', fontSize: '12px' }}
                            onClick={() => handleStartEdit(key, value || '')}
                          >
                            {value || t('editProduct.clickToAddValue')}
                          </Text>
                        )}
                      </Box>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => handleDeleteKeyValue(key)}
                      >
                        <IconMinus size={14} />
                      </ActionIcon>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </>
        )}

        {/* Add new specification */}
        <Box>
          <TextInput
            placeholder={t('editProduct.addNewSpecification')}
            value={newKey}
            onChange={(event) => {
              setNewKey(event.currentTarget.value);
              // Clear validation error when user types
              if (keyValidationError) {
                setKeyValidationError(null);
              }
            }}
            size="sm"
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newKey.trim()) {
                event.preventDefault();
                handleAddKey();
              }
            }}
            rightSection={
              <ActionIcon size="sm" onClick={handleAddKey} disabled={!newKey.trim()} color="blue">
                <IconPlus size={14} />
              </ActionIcon>
            }
          />
        </Box>

        {dataEntries.length === 0 && (
          <Box style={{ textAlign: 'center', padding: '20px' }}>
            <Text size="sm" c="dimmed">
              {t('editProduct.noSpecificationsAdded')}
            </Text>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
